import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {EmTranslatePipe, FeatureFlagService, TranslationService} from '@emma-labs/shared/angular';
import {
  Country,
  COUNTRY_OPTIONS,
  ENVIRONMENT,
  EnvironmentBroker,
  FeatureFlags,
  Gender,
  Language,
  LANGUAGE_OPTIONS,
  Province,
  PROVINCE_POSSIBILITY,
  SEX_OPTIONS,
  STATE_OPTIONS,
  SUPPORTED_PROVINCE_POSSIBILITY,
} from '@emma-labs/shared/typescript';
import {UntilDestroy} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {Button} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {first} from 'rxjs/operators';
import {ClientInfo} from '../../../entities/underwriting-page-object/underwriting-page-object';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {TextIndicatorComponent} from '../../text-indicator/text-indicator.component';

interface ClientInfoSectionForm {
  firstName: FormControl<string | null>;
  middleName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthday: FormControl<string | null>;
  sex: FormControl<Gender | null>;
  birthCountry: FormControl<string | null>;
  birthState: FormControl<string | null>;
  preferredLanguage: FormControl<Language | null>;
  email: FormControl<string | null>;
  phone: FormControl<string | null>;
  province: FormControl<Province | null>;
  address: FormControl<string | null>;
  unitNumber: FormControl<string | null>;
  city: FormControl<string | null>;
  postalCode: FormControl<string | null>;
  salary: FormControl<number | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-client-info-section',
  templateUrl: './client-info-section.component.html',
  imports: [
    CardModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    NgxMaskDirective,
    TextIndicatorComponent,
    Button,
    EmTranslatePipe,
  ],
})
export class ClientInfoSectionComponent extends BaseSectionComponent<
  FormGroup<ClientInfoSectionForm>,
  ClientInfo
> {
  readonly sexOptions = SEX_OPTIONS;
  readonly languageOptions = LANGUAGE_OPTIONS;
  readonly currentProvinceOptions = SUPPORTED_PROVINCE_POSSIBILITY;
  readonly countriesOptions = COUNTRY_OPTIONS;
  readonly stateOptions = STATE_OPTIONS;
  readonly provinceOptions = PROVINCE_POSSIBILITY;
  readonly country = Country;
  readonly isFr: boolean;
  age!: number;
  isDownloadingReplacementNotice = false;
  hasPolicyReplacementNotice = false;
  isPolicyReplacementDownloadEnabled = false;

  @Input({required: true})
  override set props(clientInfo: ClientInfo) {
    if (!this.isPropsReceived && clientInfo) {
      super.props = clientInfo;

      if (!clientInfo.unitNumber) {
        this.form.controls.unitNumber.setValue('--', {emitEvent: false});
      }

      this.age = clientInfo.age;
      this.hasPolicyReplacementNotice = clientInfo.hasPolicyReplacementNotice;
    }
  }

  @Output() replacementNoticeDocumentRequested = new EventEmitter<() => void>();

  constructor(
    private readonly fb: FormBuilder,
    readonly featureFlagService: FeatureFlagService,
    readonly translate: TranslationService,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
    this.isFr = translate.currentLanguageIsFr();

    featureFlagService
      .isEnabled(FeatureFlags.IS_UP_POLICY_REPLACEMENT_DOWNLOAD_ENABLED)
      .pipe(first())
      .subscribe((isEnabled) => (this.isPolicyReplacementDownloadEnabled = isEnabled));
  }

  downloadPolicyReplacementDocument(): void {
    this.isDownloadingReplacementNotice = true;
    this.replacementNoticeDocumentRequested.emit(() => (this.isDownloadingReplacementNotice = false));
  }

  private createForm(): FormGroup<ClientInfoSectionForm> {
    return this.fb.group<ClientInfoSectionForm>({
      firstName: this.fb.control(null),
      middleName: this.fb.control(null),
      lastName: this.fb.control(null),
      birthday: this.fb.control(null),
      sex: this.fb.control(null),
      birthCountry: this.fb.control(null),
      birthState: this.fb.control(null),
      preferredLanguage: this.fb.control(null),
      email: this.fb.control(null),
      phone: this.fb.control(null),
      province: this.fb.control(null),
      address: this.fb.control(null),
      unitNumber: this.fb.control(null),
      city: this.fb.control(null),
      postalCode: this.fb.control(null),
      salary: this.fb.control(null),
    });
  }
}
